/**
 * Small DOM helpers replacing the jQuery calls used across the Blade views.
 * Every function accepts either a selector string or an Element.
 */

export function qs(target, root = document) {
    if (target == null) return null;
    return typeof target === 'string' ? root.querySelector(target) : target;
}

export function qsa(target, root = document) {
    if (target == null) return [];
    if (typeof target === 'string') return Array.from(root.querySelectorAll(target));
    if (target instanceof Element || target === document || target === window) return [target];
    return Array.from(target);
}

/**
 * Bind an event. With a selector it is delegated, like `$(el).on(evt, selector, fn)`:
 * the handler runs with `this` set to the matched element.
 * Returns a function that removes the listener.
 */
export function on(target, events, selector, handler, options) {
    if (typeof selector === 'function') {
        options = handler;
        handler = selector;
        selector = null;
    }
    const els = qsa(target);
    // Like jQuery, a handler returning false cancels the default action and stops propagation.
    const run = (ctx, e) => {
        if (handler.call(ctx, e) === false) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    const listener = selector
        ? function (e) {
            const match = e.target instanceof Element ? e.target.closest(selector) : null;
            if (match && this.contains(match)) run(match, e);
        }
        : function (e) {
            run(this, e);
        };
    const types = events.split(/\s+/).filter(Boolean);
    els.forEach((el) => types.forEach((t) => el.addEventListener(t, listener, options)));
    return () => els.forEach((el) => types.forEach((t) => el.removeEventListener(t, listener, options)));
}

/**
 * `$(sel).off(evt).on(evt, fn)`: bind fn, replacing whatever this function bound earlier
 * for the same event on each element, so re-running init code never stacks handlers.
 */
export function rebind(target, events, handler) {
    const types = events.split(/\s+/).filter(Boolean);
    qsa(target).forEach((el) => {
        el.__rebound = el.__rebound || {};
        types.forEach((t) => {
            if (el.__rebound[t]) el.removeEventListener(t, el.__rebound[t]);
            const listener = function (e) {
                if (handler.call(this, e) === false) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };
            el.__rebound[t] = listener;
            el.addEventListener(t, listener);
        });
    });
}

/** `$(sel).off(evt)` for handlers bound with rebind(). */
export function unbind(target, events) {
    qsa(target).forEach((el) => {
        if (!el.__rebound) return;
        const types = events ? events.split(/\s+/).filter(Boolean) : Object.keys(el.__rebound);
        types.forEach((t) => {
            if (el.__rebound[t]) el.removeEventListener(t, el.__rebound[t]);
            delete el.__rebound[t];
        });
    });
}

export function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
}

export function show(target, display = '') {
    qsa(target).forEach((el) => {
        el.style.display = display;
    });
}

export function hide(target) {
    qsa(target).forEach((el) => {
        el.style.display = 'none';
    });
}

const fadeState = new WeakMap();

function isHidden(el) {
    return getComputedStyle(el).display === 'none';
}

/** Fade out then set display:none, like `$(el).fadeOut()` (400ms default). */
export function fadeOut(target, duration = 400) {
    qsa(target).forEach((el) => {
        const state = fadeState.get(el);
        if (state === 'out' || (!state && isHidden(el))) return;
        fadeState.set(el, 'out');
        el.style.transition = `opacity ${duration}ms`;
        el.style.opacity = '0';
        setTimeout(() => {
            if (fadeState.get(el) !== 'out') return;
            fadeState.delete(el);
            el.style.display = 'none';
            el.style.opacity = '';
            el.style.transition = '';
        }, duration);
    });
}

/** Show then fade in, like `$(el).fadeIn()`. */
export function fadeIn(target, duration = 400, display = '') {
    qsa(target).forEach((el) => {
        const state = fadeState.get(el);
        if (state === 'in' || (!state && !isHidden(el))) return;
        fadeState.set(el, 'in');
        if (isHidden(el)) {
            el.style.opacity = '0';
            el.style.display = display;
            if (isHidden(el)) el.style.display = 'block';
            void el.offsetWidth; // flush so the transition starts from 0
        }
        el.style.transition = `opacity ${duration}ms`;
        el.style.opacity = '1';
        setTimeout(() => {
            if (fadeState.get(el) !== 'in') return;
            fadeState.delete(el);
            el.style.transition = '';
            el.style.opacity = '';
        }, duration);
    });
}

/** Parse an HTML string into its top-level nodes. */
export function parseHTML(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = String(html).trim();
    return tpl.content;
}

/**
 * Replace an element's content and execute any <script> it contains,
 * matching jQuery's `.html()` / `.load()` behaviour. Scripts run in document order.
 */
export function setHTML(target, html) {
    const el = qs(target);
    if (!el) return;
    el.innerHTML = html;
    runScripts(el);
}

/** Append HTML (and run its scripts), like `$(el).append(html)`. */
export function appendHTML(target, html) {
    const el = qs(target);
    if (!el) return;
    const frag = parseHTML(html);
    const scripts = Array.from(frag.querySelectorAll('script'));
    el.appendChild(frag);
    scripts.forEach(execScript);
}

export function runScripts(root) {
    root.querySelectorAll('script').forEach(execScript);
}

function execScript(old) {
    const type = old.getAttribute('type');
    if (type && !/^(text|application)\/(javascript|ecmascript)$|^module$/i.test(type)) return;
    const s = document.createElement('script');
    Array.from(old.attributes).forEach((a) => s.setAttribute(a.name, a.value));
    s.text = old.textContent;
    old.replaceWith(s);
}

/*
 * text / attr / prop / html: getters read the first match ('' / undefined when none),
 * setters write every match. No-ops on empty selections, like jQuery.
 */
export function text(target, ...value) {
    const els = qsa(target);
    if (!value.length) return els[0] ? els[0].textContent : '';
    els.forEach((el) => {
        el.textContent = value[0] == null ? '' : value[0];
    });
    return els;
}

export function html(target) {
    const el = qs(target);
    return el ? el.innerHTML : undefined;
}

export function attr(target, name, ...value) {
    const els = qsa(target);
    if (!value.length) return els[0] ? els[0].getAttribute(name) ?? undefined : undefined;
    els.forEach((el) => (value[0] == null ? el.removeAttribute(name) : el.setAttribute(name, value[0])));
    return els;
}

export function prop(target, name, ...value) {
    const els = qsa(target);
    if (!value.length) return els[0] ? els[0][name] : undefined;
    els.forEach((el) => {
        el[name] = value[0];
    });
    return els;
}

/** addClass / removeClass / toggleClass / hasClass over every match (space-separated class lists). */
const classList = (cls) => String(cls || '').split(/\s+/).filter(Boolean);
export function addClass(target, cls) {
    qsa(target).forEach((el) => el.classList.add(...classList(cls)));
}
export function removeClass(target, cls) {
    qsa(target).forEach((el) => (cls == null ? el.removeAttribute('class') : el.classList.remove(...classList(cls))));
}
export function toggleClass(target, cls, state) {
    qsa(target).forEach((el) => classList(cls).forEach((c) => el.classList.toggle(c, state)));
}
export function hasClass(target, cls) {
    return qsa(target).some((el) => el.classList.contains(cls));
}

/** Dispatch a DOM event on every match, like `$(el).trigger('change')` (bubbles). */
export function trigger(target, type) {
    qsa(target).forEach((el) => el.dispatchEvent(new Event(type, { bubbles: true, cancelable: true })));
}

/** A detached <div> holding `html`, like `$('<div></div>').append(html)`. */
export function div(html) {
    const el = document.createElement('div');
    el.innerHTML = html == null ? '' : String(html);
    return el;
}

/** Remove every match, like `$(sel).remove()`. */
export function remove(target) {
    qsa(target).forEach((el) => el.remove());
}

/**
 * Submit a form like `$(form).submit()` / `.trigger('submit')`: run submit listeners,
 * then submit natively unless one of them called preventDefault().
 */
export function submit(target) {
    let submitted = true;
    qsa(target).forEach((form) => {
        const ev = new Event('submit', { bubbles: true, cancelable: true });
        if (form.dispatchEvent(ev)) form.submit();
        else submitted = false;
    });
    return submitted;
}

/**
 * jQuery.each: iterate arrays / array-likes (index, value) or objects (key, value),
 * with `this` = value; returning false stops the loop.
 */
export function each(collection, fn) {
    if (collection == null) return collection;
    const isList = Array.isArray(collection) || typeof collection.length === 'number';
    if (isList) {
        for (let i = 0; i < collection.length; i++) {
            if (fn.call(collection[i], i, collection[i]) === false) break;
        }
    } else {
        for (const key of Object.keys(collection)) {
            if (fn.call(collection[key], key, collection[key]) === false) break;
        }
    }
    return collection;
}

/** Serialize a form like `$(form).serialize()` (URL-encoded). */
export function serialize(form) {
    return new URLSearchParams(new FormData(qs(form))).toString();
}
